import { ForwardedRef, useCallback, useRef } from 'react';

type FieldElement = HTMLInputElement | HTMLTextAreaElement;

export const useFieldRef = <T extends FieldElement>(
  ref: ForwardedRef<T>,
  onSync?: (node: T) => void
) => {
  const elementRef = useRef<T | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  const onSyncRef = useRef(onSync);
  onSyncRef.current = onSync;

  const handleFormReset = useCallback(() => {
    queueMicrotask(() => {
      if (elementRef.current) {
        onSyncRef.current?.(elementRef.current);
      }
    });
  }, []);

  const attachRef = useCallback(
    (node: T | null) => {
      formRef.current?.removeEventListener('reset', handleFormReset);
      formRef.current = node?.form ?? null;

      if (onSyncRef.current) {
        formRef.current?.addEventListener('reset', handleFormReset);
      }

      elementRef.current = node;

      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }

      if (node) {
        onSyncRef.current?.(node);
      }
    },
    [ref, handleFormReset]
  );

  return { elementRef, attachRef };
};
